import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import MentionInput from './MentionInput';
import ImageCropModal from './ImageCropModal';
import { resolveMediaUrl, getYouTubeId } from './PostCard';
import {
  Send, Image, FileText, Link2, Mic, Video, Package, Users, X, Plus,
  ChevronRight, BarChart3, Check, Loader2, Youtube,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const URL_REGEX = /https?:\/\/[^\s<>"')\]]+/gi;

/* Brief 27 oct A.3 (RCA) — le mur de profil avait son propre mini-composeur
   (texte seul) : composant UNIQUE Feed + Profil. A.4 — un lien collé dans le
   texte est détecté automatiquement (aperçu OG / YouTube / image / PDF) sans
   passer par « Lien » puis « + ». */
const PostComposer = ({ onPosted, variant = 'feed', placeholder }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [newPost, setNewPost] = useState('');
  const [posting, setPosting] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [linkInput, setLinkInput] = useState('');
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [myProducts, setMyProducts] = useState([]);
  const [myCommunities, setMyCommunities] = useState([]);
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [showCommunityPicker, setShowCommunityPicker] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [showPollCreator, setShowPollCreator] = useState(false);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [pollAllowMultiple, setPollAllowMultiple] = useState(false);
  const fileInputRef = useRef(null);
  const ignoredUrls = useRef(new Set());

  useEffect(() => {
    if (!user?.user_id) return;
    axios.get(`${API}/users/${user.user_id}/products`, { withCredentials: true })
      .then((r) => setMyProducts(r.data.products || [])).catch(() => {});
    axios.get(`${API}/users/${user.user_id}/communities`, { withCredentials: true })
      .then((r) => setMyCommunities((r.data.communities || []).filter((c) => c.is_member !== false))).catch(() => {});
  }, [user?.user_id]);

  const fetchOg = async (link) => {
    try {
      const { data } = await axios.post(`${API}/og/fetch`, { url: link }, { withCredentials: true });
      if (data.title || data.image || data.type) {
        setAttachments((prev) => prev.map((att) => (att.type === 'link' && att.url === link ? { ...att, og: data } : att)));
      }
    } catch {}
  };

  // A.4 — détection automatique des URLs dans le texte (debounce 500 ms).
  useEffect(() => {
    const t = setTimeout(() => {
      const found = Array.from(new Set((newPost.match(URL_REGEX) || []).map((u) => u.replace(/[.,;:!?]+$/, ''))));
      setAttachments((prev) => {
        let next = prev.filter((att) => !(att.type === 'link' && att.auto && !found.includes(att.url)));
        const hasMedia = next.some((att) => att.type === 'media');
        const hasLink = next.some((att) => att.type === 'link');
        if (!hasMedia && !hasLink) {
          const candidate = found.find((u) => !ignoredUrls.current.has(u));
          if (candidate) {
            next = [...next, { type: 'link', url: candidate, auto: true, og: null }];
            fetchOg(candidate);
          }
        }
        return next;
      });
    }, 500);
    return () => clearTimeout(t);
  }, [newPost]); // eslint-disable-line react-hooks/exhaustive-deps

  const reset = () => {
    setNewPost(''); setAttachments([]); setShowLinkInput(false); setLinkInput('');
    setShowPollCreator(false); setPollQuestion(''); setPollOptions(['', '']); setPollAllowMultiple(false);
    ignoredUrls.current = new Set();
  };

  const handleCreatePost = async () => {
    if ((!newPost.trim() && attachments.length === 0) || posting) return;
    setPosting(true);
    try {
      const media = attachments.find((a) => a.type === 'media');
      const link = attachments.find((a) => a.type === 'link');
      const product = attachments.find((a) => a.type === 'product');
      const community = attachments.find((a) => a.type === 'community');
      let content = newPost;
      if (product) content += `\n\n[product:${product.id}:${product.title}]`;
      if (community) content += `\n\n[community:${community.id}:${community.title}]`;
      const mediaUrl = media ? media.url : link ? link.url : null;
      const ogData = !media && link ? link.og || null : null;
      await axios.post(`${API}/posts`, { content, media_url: mediaUrl, og_data: ogData }, { withCredentials: true });
      reset();
      toast.success('Publication partagée');
      onPosted && onPosted();
    } catch (error) {
      toast.error("La publication n'a pas pu être envoyée. Réessaie.");
    } finally {
      setPosting(false);
    }
  };

  const handleCreatePoll = async () => {
    const validOptions = pollOptions.filter((o) => o.trim());
    if (!pollQuestion.trim() || validOptions.length < 2 || posting) return;
    setPosting(true);
    try {
      await axios.post(`${API}/feed-polls`, {
        question: pollQuestion, options: validOptions, content: newPost, allow_multiple: pollAllowMultiple,
      }, { withCredentials: true });
      reset();
      toast.success('Sondage publié');
      onPosted && onPosted();
    } catch (err) {
      toast.error("Le sondage n'a pas pu être publié.");
    } finally {
      setPosting(false);
    }
  };

  const addLinkAttachment = async () => {
    const link = linkInput.trim();
    if (!link) return;
    setAttachments((prev) => [...prev.filter((a) => a.type !== 'link'), { type: 'link', url: link, og: null }]);
    setLinkInput('');
    setShowLinkInput(false);
    fetchOg(link);
  };

  const addMediaAttachment = (mediaType) => {
    setShowAttachMenu(false);
    if (fileInputRef.current) {
      const acceptTypes = { image: 'image/*', video: 'video/*', audio: 'audio/*', document: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt' };
      fileInputRef.current.accept = acceptTypes[mediaType] || '*/*';
      fileInputRef.current.click();
    }
  };

  const uploadMediaFile = async (file, detectedType) => {
    setUploadingMedia(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true });
      if (response.data.file_url) {
        setAttachments((prev) => [...prev.filter((a) => a.type !== 'media' && !(a.type === 'link' && a.auto)), { type: 'media', mediaType: detectedType, url: response.data.file_url, name: file.name }]);
      }
    } catch (error) {
      toast.error("Le fichier n'a pas pu être envoyé.");
    } finally {
      setUploadingMedia(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    let detectedType = 'document';
    if (file.type.startsWith('image/')) detectedType = 'image';
    else if (file.type.startsWith('video/')) detectedType = 'video';
    else if (file.type.startsWith('audio/')) detectedType = 'audio';
    if (detectedType === 'image') {
      const reader = new FileReader();
      reader.onload = () => setCropSrc(reader.result);
      reader.readAsDataURL(file);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    await uploadMediaFile(file, detectedType);
  };

  const addProductAttachment = (product) => {
    setAttachments((prev) => [...prev.filter((a) => a.type !== 'product'), { type: 'product', id: product.product_id, title: product.title, userId: product.user_id || user?.user_id }]);
    setShowProductPicker(false);
  };

  const addCommunityAttachment = (community) => {
    setAttachments((prev) => [...prev.filter((a) => a.type !== 'community'), { type: 'community', id: community.community_id, title: community.name }]);
    setShowCommunityPicker(false);
  };

  const removeAttachment = (index) => {
    const att = attachments[index];
    if (att?.type === 'link' && att.auto) ignoredUrls.current.add(att.url);
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const removeBtn = (idx, cls = '') => (
    <button type="button" onClick={(e) => { e.stopPropagation(); removeAttachment(idx); }} className={`p-1.5 rounded-full bg-black/50 text-white hover:bg-black/70 ${cls}`} aria-label="Retirer" data-testid={`attachment-remove-${idx}`}>
      <X className="h-3.5 w-3.5" />
    </button>
  );

  const toolBtn = 'flex items-center gap-1.5 px-2.5 sm:px-3 py-2 rounded-lg transition-all text-gray-500 dark:text-gray-400';
  // Variante « wall » (colonne profil ~570px à 1440px) : icônes seules + title, libellés dès 2xl, pour garder « Publier » sur la même ligne.
  const labelCls = variant === 'wall' ? 'text-sm hidden 2xl:inline' : 'text-sm hidden sm:inline';
  const canPost = showPollCreator
    ? (pollQuestion.trim() && pollOptions.filter((o) => o.trim()).length >= 2 && !posting)
    : ((newPost.trim() || attachments.length > 0) && !posting && !uploadingMedia);

  return (
    <Card className="border-2 border-purple-100 dark:border-gray-700 dark:bg-gray-800" data-testid={`post-composer-${variant}`}>
      <CardContent className="pt-5 pb-4">
        <input type="file" ref={fileInputRef} onChange={handleFileSelect} className="hidden" accept="image/*,video/*,.pdf,.doc,.docx" />
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <Avatar className="h-10 w-10 flex-shrink-0 hidden sm:flex">
              <AvatarImage src={resolveMediaUrl(user?.avatar)} />
              <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white text-sm">{user?.display_name?.substring(0, 2).toUpperCase()}</AvatarFallback>
            </Avatar>
            <MentionInput
              value={newPost}
              onChange={setNewPost}
              placeholder={placeholder || 'Que veux-tu partager ? Colle un lien, il sera reconnu automatiquement. @ pour mentionner.'}
              className="min-h-[88px] border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl p-3 focus:border-purple-400 focus:ring-1 focus:ring-purple-400"
              minRows={3}
              data-testid="create-post-input"
            />
          </div>

          {attachments.length > 0 && (
            <div className="space-y-3 p-3 bg-gray-50 dark:bg-gray-900/40 rounded-xl" data-testid="attachments-preview">
              {attachments.map((att, idx) => (
                <div key={idx} className="relative">
                  {att.type === 'media' && att.mediaType === 'image' && (
                    <div className="relative rounded-lg overflow-hidden">
                      <img src={resolveMediaUrl(att.url)} alt="Aperçu" className="w-full max-h-[240px] object-cover" data-testid="attachment-image-preview" />
                      {removeBtn(idx, 'absolute top-2 right-2')}
                    </div>
                  )}
                  {att.type === 'media' && att.mediaType === 'video' && (
                    <div className="relative rounded-lg overflow-hidden bg-black">
                      <video src={resolveMediaUrl(att.url)} className="w-full max-h-[240px]" controls />
                      {removeBtn(idx, 'absolute top-2 right-2')}
                    </div>
                  )}
                  {att.type === 'media' && att.mediaType === 'audio' && (
                    <div className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                      <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center"><Mic className="h-5 w-5 text-orange-600" /></div>
                      <div className="flex-1 min-w-0"><p className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">{att.name}</p><audio src={resolveMediaUrl(att.url)} controls className="w-full h-8" /></div>
                      {removeBtn(idx)}
                    </div>
                  )}
                  {att.type === 'media' && att.mediaType === 'document' && (
                    <div className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center"><FileText className="h-5 w-5 text-blue-600" /></div>
                      <div className="flex-1 min-w-0"><p className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">{att.name}</p><p className="text-xs text-gray-500">Document prêt à publier</p></div>
                      {removeBtn(idx)}
                    </div>
                  )}
                  {att.type === 'link' && (() => {
                    const yt = getYouTubeId(att.url);
                    const kind = att.og?.type;
                    return (
                      <div className="relative bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden" data-testid={att.auto ? 'attachment-link-auto' : 'attachment-link'}>
                        {yt ? (
                          <div className="flex">
                            <img src={`https://img.youtube.com/vi/${yt}/hqdefault.jpg`} alt="" className="w-32 h-20 object-cover flex-shrink-0" />
                            <div className="flex-1 p-3 min-w-0">
                              <p className="text-xs text-red-600 font-semibold flex items-center gap-1"><Youtube className="h-3.5 w-3.5" /> YouTube — lecteur intégré</p>
                              <p className="text-sm font-medium text-gray-900 dark:text-white line-clamp-1 mt-0.5">{att.og?.title || att.url}</p>
                            </div>
                          </div>
                        ) : kind === 'image' || att.og?.image ? (
                          <div className="flex">
                            <img src={att.og.image} alt="" className="w-24 h-24 object-cover flex-shrink-0" />
                            <div className="flex-1 p-3 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white line-clamp-1">{att.og.title || att.url}</p>
                              {att.og.description && <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">{att.og.description}</p>}
                              <p className="text-xs text-purple-600 mt-1">{kind === 'image' ? 'Image' : att.og.domain || att.url}</p>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-3 p-3">
                            <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/40 rounded-lg flex items-center justify-center flex-shrink-0">
                              {kind === 'document' ? <FileText className="h-5 w-5 text-blue-600" /> : <Link2 className="h-5 w-5 text-purple-600" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{att.og?.title || att.url}</p>
                              <p className="text-xs text-purple-600">{kind === 'document' ? 'Document' : att.og?.domain || (att.og === null ? 'Aperçu en cours…' : 'Lien')}</p>
                            </div>
                          </div>
                        )}
                        {removeBtn(idx, 'absolute top-2 right-2')}
                      </div>
                    );
                  })()}
                  {att.type === 'product' && (
                    <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl border border-purple-200 dark:border-purple-800/40 cursor-pointer" onClick={() => navigate(`/app/shop/${att.userId}?product=${att.id}`)}>
                      <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/40 rounded-lg flex items-center justify-center"><Package className="h-5 w-5 text-purple-600" /></div>
                      <div className="flex-1 min-w-0"><p className="text-sm font-semibold text-purple-900 dark:text-purple-100">{att.title}</p><p className="text-xs text-purple-500">Produit de ma boutique</p></div>
                      <ChevronRight className="h-4 w-4 text-purple-400" />
                      {removeBtn(idx)}
                    </div>
                  )}
                  {att.type === 'community' && (
                    <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-cyan-50 to-teal-50 dark:from-cyan-900/20 dark:to-teal-900/20 rounded-xl border border-cyan-200 dark:border-cyan-800/40 cursor-pointer" onClick={() => navigate(`/app/community/${att.id}`)}>
                      <div className="w-10 h-10 bg-cyan-100 dark:bg-cyan-900/40 rounded-lg flex items-center justify-center"><Users className="h-5 w-5 text-cyan-600" /></div>
                      <div className="flex-1 min-w-0"><p className="text-sm font-semibold text-cyan-900 dark:text-cyan-100">{att.title}</p><p className="text-xs text-cyan-500">Communauté</p></div>
                      <ChevronRight className="h-4 w-4 text-cyan-400" />
                      {removeBtn(idx)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {uploadingMedia && (
            <div className="flex items-center gap-2 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg text-purple-700 dark:text-purple-300" data-testid="upload-progress">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Envoi du fichier…</span>
            </div>
          )}

          {showLinkInput && (
            <div className="flex gap-2">
              <Input value={linkInput} onChange={(e) => setLinkInput(e.target.value)} placeholder="Coller un lien (https://…)" className="flex-1 dark:bg-gray-700 dark:text-white dark:border-gray-600" data-testid="link-input" onKeyDown={(e) => e.key === 'Enter' && addLinkAttachment()} autoFocus />
              <Button variant="outline" onClick={addLinkAttachment} data-testid="link-add-btn"><Plus className="h-4 w-4" /></Button>
              <Button variant="ghost" onClick={() => { setShowLinkInput(false); setLinkInput(''); }}><X className="h-4 w-4" /></Button>
            </div>
          )}

          <div className="flex items-center justify-between gap-2 border-t border-gray-100 dark:border-gray-700 pt-3 flex-wrap">
            <div className="flex items-center gap-0.5 flex-wrap">
              <div className="relative">
                <button type="button" onClick={() => setShowAttachMenu(!showAttachMenu)} className={`${toolBtn} hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/30`} data-testid="attach-media-btn" title="Image, vidéo, audio ou document">
                  <Image className="h-5 w-5" /><span className={labelCls}>Média</span>
                </button>
                {showAttachMenu && (
                  <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-700 rounded-xl shadow-lg border border-gray-100 dark:border-gray-600 py-2 z-10 min-w-[160px]" data-testid="attach-media-menu">
                    {[['image', Image, 'Image', 'text-pink-500'], ['video', Video, 'Vidéo', 'text-red-500'], ['audio', Mic, 'Audio', 'text-orange-500'], ['document', FileText, 'Document', 'text-blue-500']].map(([k, Icon, label, color]) => (
                      <button key={k} type="button" onClick={() => addMediaAttachment(k)} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600" data-testid={`attach-media-${k}`}>
                        <Icon className={`h-4 w-4 ${color}`} />{label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button type="button" onClick={() => setShowLinkInput(!showLinkInput)} className={`${toolBtn} ${showLinkInput ? 'text-blue-600 bg-blue-50 dark:bg-blue-900/30' : 'hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30'}`} data-testid="attach-link-btn" title="Ajouter un lien (ou colle-le simplement dans le texte)">
                <Link2 className="h-5 w-5" /><span className={labelCls}>Lien</span>
              </button>
              <button type="button" onClick={() => { setShowPollCreator(!showPollCreator); setShowProductPicker(false); setShowCommunityPicker(false); }} className={`${toolBtn} ${showPollCreator ? 'text-amber-600 bg-amber-50 dark:bg-amber-900/30' : 'hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30'}`} data-testid="attach-poll-btn" title="Sondage">
                <BarChart3 className="h-5 w-5" /><span className={labelCls}>Sondage</span>
              </button>
              <div className="relative">
                <button type="button" onClick={() => { setShowProductPicker(!showProductPicker); setShowCommunityPicker(false); }} className={`${toolBtn} ${showProductPicker ? 'text-purple-600 bg-purple-50 dark:bg-purple-900/30' : 'hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/30'}`} data-testid="attach-product-btn" title="Partager un produit de ma boutique">
                  <Package className="h-5 w-5" /><span className={labelCls}>Produit</span>
                </button>
                {showProductPicker && (
                  <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-700 rounded-xl shadow-lg border border-gray-100 dark:border-gray-600 py-2 z-10 min-w-[220px] max-h-[200px] overflow-y-auto" data-testid="product-picker">
                    {myProducts.length === 0 ? <p className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">Aucun produit dans ta boutique</p> : myProducts.map((product) => (
                      <button key={product.product_id} type="button" onClick={() => addProductAttachment(product)} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600" data-testid={`product-picker-${product.product_id}`}>
                        {product.thumbnail_url ? <img src={product.thumbnail_url} alt="" className="w-8 h-8 rounded object-cover" /> : <div className="w-8 h-8 rounded bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center"><Package className="h-4 w-4 text-purple-500" /></div>}
                        <span className="truncate">{product.title}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="relative">
                <button type="button" onClick={() => { setShowCommunityPicker(!showCommunityPicker); setShowProductPicker(false); }} className={`${toolBtn} ${showCommunityPicker ? 'text-green-600 bg-green-50 dark:bg-green-900/30' : 'hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30'}`} data-testid="attach-community-btn" title="Mentionner une communauté">
                  <Users className="h-5 w-5" /><span className={labelCls}>Communauté</span>
                </button>
                {showCommunityPicker && (
                  <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-700 rounded-xl shadow-lg border border-gray-100 dark:border-gray-600 py-2 z-10 min-w-[220px] max-h-[200px] overflow-y-auto" data-testid="community-picker">
                    {myCommunities.length === 0 ? <p className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">Aucune communauté</p> : myCommunities.map((community) => (
                      <button key={community.community_id} type="button" onClick={() => addCommunityAttachment(community)} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600" data-testid={`community-picker-${community.community_id}`}>
                        {community.avatar_url ? <img src={community.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" /> : <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center"><Users className="h-4 w-4 text-green-500" /></div>}
                        <span className="truncate">{community.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <Button onClick={showPollCreator ? handleCreatePoll : handleCreatePost} disabled={!canPost} className="bg-gradient-to-r from-purple-600 to-pink-600 text-white" data-testid="create-post-button">
              <Send className="h-4 w-4 mr-2" />{posting ? 'Publication…' : showPollCreator ? 'Publier le sondage' : 'Publier'}
            </Button>
          </div>

          {showPollCreator && (
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-700 space-y-3" data-testid="poll-creator">
              <div className="flex items-center gap-2 mb-1">
                <BarChart3 className="h-5 w-5 text-amber-600" />
                <span className="font-semibold text-amber-800 dark:text-amber-300">Créer un sondage</span>
                <button type="button" onClick={() => setShowPollCreator(false)} className="ml-auto text-gray-400 hover:text-gray-600" aria-label="Fermer"><X className="h-4 w-4" /></button>
              </div>
              <Input value={pollQuestion} onChange={(e) => setPollQuestion(e.target.value)} placeholder="Ta question…" className="bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600" data-testid="poll-question-input" />
              {pollOptions.map((opt, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input value={opt} onChange={(e) => { const next = [...pollOptions]; next[i] = e.target.value; setPollOptions(next); }} placeholder={`Option ${i + 1}`} className="bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600" data-testid={`poll-option-input-${i}`} />
                  {pollOptions.length > 2 && (
                    <button type="button" onClick={() => setPollOptions(pollOptions.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500" aria-label="Retirer l'option"><X className="h-4 w-4" /></button>
                  )}
                </div>
              ))}
              {pollOptions.length < 6 && (
                <button type="button" onClick={() => setPollOptions([...pollOptions, ''])} className="flex items-center gap-1 text-sm text-amber-600 dark:text-amber-400 hover:text-amber-700" data-testid="poll-add-option-btn">
                  <Plus className="h-4 w-4" /> Ajouter une option
                </button>
              )}
              <label className="flex items-center gap-2 cursor-pointer" data-testid="poll-allow-multiple-label">
                <input type="checkbox" checked={pollAllowMultiple} onChange={(e) => setPollAllowMultiple(e.target.checked)} className="rounded border-gray-300 text-amber-600 focus:ring-amber-500" data-testid="poll-allow-multiple-checkbox" />
                <span className="text-sm text-amber-800 dark:text-amber-300 flex items-center gap-1"><Check className="h-3.5 w-3.5" /> Autoriser plusieurs choix</span>
              </label>
            </div>
          )}
        </div>
      </CardContent>

      <ImageCropModal
        isOpen={!!cropSrc}
        imageSrc={cropSrc}
        onClose={() => setCropSrc(null)}
        aspect={4 / 3}
        aspectOptions={[
          { label: 'Carré', value: 1 },
          { label: '4:3', value: 4 / 3 },
          { label: '16:9', value: 16 / 9 },
          { label: 'Portrait 4:5', value: 4 / 5 },
        ]}
        title="Recadrer l'image du post"
        onCropComplete={(file) => { setCropSrc(null); uploadMediaFile(file, 'image'); }}
      />
    </Card>
  );
};

export default PostComposer;
